import React, { useState, useMemo } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Modal } from 'react-bootstrap';
import * as XLSX from 'xlsx';

const APIProviderCommission = () => {
  // Sample commission data generator
  const generateCommissionData = (count = 30) => {
    const providers = ['PaySprint', 'FinoBank', 'RazorPay', 'PayTM', 'PhonePe', 'Cyrus', 'Roundpay'];
    const services = ['Recharge', 'Bill Payment', 'Money Transfer', 'AEPS', 'Pan Card', 'Fastag', 'DMT'];
    const operators = ['Airtel', 'Jio', 'Vi', 'BSNL', 'Electricity', 'Gas', 'Water', 'DTH'];
    
    return Array.from({ length: count }, (_, i) => ({
      id: `COM${String(i + 1).padStart(4, '0')}`,
      provider: providers[Math.floor(Math.random() * providers.length)],
      service: services[Math.floor(Math.random() * services.length)],
      operator: operators[Math.floor(Math.random() * operators.length)],
      commissionType: Math.random() > 0.5 ? 'Percentage' : 'Fixed',
      commissionValue: (Math.random() * 5).toFixed(2),
      minAmount: Math.floor(Math.random() * 100) + 10,
      maxAmount: Math.floor(Math.random() * 5000) + 1000,
      status: Math.random() > 0.2 ? 'Active' : 'Inactive'
    }));
  };

  const [commissionData, setCommissionData] = useState(generateCommissionData(30));
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(8);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedCommission, setSelectedCommission] = useState(null);
  
  const [formData, setFormData] = useState({
    provider: '',
    service: '',
    operator: '',
    commissionType: 'Percentage',
    commissionValue: '',
    minAmount: '',
    maxAmount: '',
    status: 'Active'
  });

  // Filter data based on search
  const filteredData = useMemo(() => {
    let filtered = [...commissionData];

    if (searchQuery) {
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    return filtered;
  }, [commissionData, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Export to Excel
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'API Provider Commission');
    XLSX.writeFile(workbook, `API_Provider_Commission_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Handle Add Commission
  const handleAddCommission = () => {
    setModalMode('add');
    setSelectedCommission(null);
    setFormData({
      provider: '',
      service: '',
      operator: '',
      commissionType: 'Percentage',
      commissionValue: '',
      minAmount: '',
      maxAmount: '',
      status: 'Active'
    });
    setShowModal(true);
  };

  // Handle Edit Commission
  const handleEditCommission = (commission) => {
    setModalMode('edit');
    setSelectedCommission(commission);
    setFormData({
      provider: commission.provider,
      service: commission.service,
      operator: commission.operator,
      commissionType: commission.commissionType,
      commissionValue: commission.commissionValue,
      minAmount: commission.minAmount,
      maxAmount: commission.maxAmount,
      status: commission.status
    });
    setShowModal(true);
  };

  // Handle Save Commission
  const handleSaveCommission = () => {
    if (modalMode === 'add') {
      const maxId = commissionData.reduce((max, item) => {
        const idNum = parseInt(item.id.replace(/\D/g, ''));
        return idNum > max ? idNum : max;
      }, 0);
      const newId = `COM${String(maxId + 1).padStart(4, '0')}`;
      
      const newCommission = {
        id: newId,
        ...formData
      };
      
      setCommissionData([...commissionData, newCommission]);
    } else if (modalMode === 'edit') {
      const updatedData = commissionData.map(item => 
        item.id === selectedCommission.id ? { ...item, ...formData } : item
      );
      setCommissionData(updatedData);
    }
    
    setShowModal(false);
  };


  // Pagination component
  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      />
    );

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  return (
    <Container fluid className="">
      <div className="user-table-container">
        {/* Header */}
        <Row className="mb-3 g-2 align-items-center">
          <Col lg={3} md={4} sm={12}>
            <h4 className="mb-0" style={{ color: '#1e293b', fontWeight: '700', fontSize: '1.5rem' }}>
              API Provider Commission
            </h4>
          </Col>
          <Col lg={9} md={8} sm={12} className="d-flex justify-content-end gap-2">
            <InputGroup style={{ maxWidth: '250px' }}>
              <InputGroup.Text>
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </InputGroup>
            <Button 
              size="sm"
              onClick={handleAddCommission}
              className="btn-outline-primary-custom"
              style={{ 
                padding: '0.4rem 1rem 0.5rem 1rem',
                fontSize: '0.875rem',
                height: 'auto'
              }}
            >
              <i className="bi bi-plus-circle me-1"></i> Add Commission
            </Button>
            <Button 
              size="sm" 
              variant="success" 
              onClick={handleExportExcel}
              style={{
                padding: '0.4rem 1rem 0.5rem 1rem',
                fontSize: '0.875rem',
                height: 'auto'
              }}
            >
              <i className="bi bi-file-earmark-excel me-1"></i> Excel
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <div className="table-responsive" style={{ background: '#f0f2f5', borderRadius: '12px', padding: '0' }}>
          <Table bordered hover className="modern-table" style={{ background: '#f0f2f5' }}>
            <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <tr>
                <th>Actions</th>
                <th>ID</th>
                <th>Provider</th>
                <th>Service</th>
                <th>Operator</th>
                <th>Commission Type</th>
                <th>Commission Value</th>
                <th>Min Amount</th>
                <th>Max Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((row, index) => (
                  <tr key={index} style={{ background: '#f0f2f5' }}>
                  <td>
                    <div className="d-flex gap-1 justify-content-center align-items-center">
                      <Button 
                        size="sm" 
                        title="Edit"
                        onClick={() => handleEditCommission(row)}
                        className="btn-outline-primary-custom"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                    </div>
                  </td>
                  <td>{row.id}</td>
                  <td>{row.provider}</td>
                  <td>{row.service}</td>
                  <td>{row.operator}</td>
                  <td>{row.commissionType}</td>
                  <td>
                    <strong style={{ color: '#667eea' }}>
                      {row.commissionType === 'Percentage' ? `${row.commissionValue}%` : `₹${row.commissionValue}`}
                    </strong>
                  </td>
                  <td>₹{row.minAmount}</td>
                  <td>₹{row.maxAmount}</td>
                  <td>{row.status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center py-4">
                    <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                    <span className="text-muted">No commission data found</span>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination Controls */}
        <Row className="align-items-center mt-4 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
          <Col lg={6} md={6} sm={12} className="mb-2 mb-md-0">
            <span style={{ 
              fontSize: '0.9rem', 
              color: '#64748b',
              fontWeight: '500'
            }}>
              Showing <strong style={{ color: '#667eea' }}>{startIndex + 1}</strong> to <strong style={{ color: '#667eea' }}>{Math.min(endIndex, filteredData.length)}</strong> of <strong style={{ color: '#667eea' }}>{filteredData.length}</strong> entries
            </span>
          </Col>
          <Col lg={6} md={6} sm={12} className="d-flex justify-content-end">
            <Pagination className="mb-0">{renderPagination()}</Pagination>
          </Col>
        </Row>
      </div>

      {/* Add/Edit Commission Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'rgb(83, 103, 254)', color: 'white', border: 'none' }}>
          <Modal.Title>
            <i className={`bi ${modalMode === 'add' ? 'bi-plus-circle' : 'bi-pencil-square'} me-2`}></i>
            {modalMode === 'add' ? 'Add New Commission' : 'Edit Commission'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Provider <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  placeholder="Enter provider name"
                />
              </Form.Group>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Service <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                >
                  <option value="">Select Service</option>
                  <option value="Recharge">Recharge</option>
                  <option value="Bill Payment">Bill Payment</option>
                  <option value="Money Transfer">Money Transfer</option>
                  <option value="AEPS">AEPS</option>
                  <option value="Pan Card">Pan Card</option>
                  <option value="Fastag">Fastag</option>
                  <option value="DMT">DMT</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Operator <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.operator}
                  onChange={(e) => setFormData({ ...formData, operator: e.target.value })}
                  placeholder="Enter operator name"
                />
              </Form.Group>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Commission Type <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={formData.commissionType}
                  onChange={(e) => setFormData({ ...formData, commissionType: e.target.value })}
                >
                  <option value="Percentage">Percentage</option>
                  <option value="Fixed">Fixed</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Commission Value <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.commissionValue}
                  onChange={(e) => setFormData({ ...formData, commissionValue: e.target.value })}
                  placeholder={formData.commissionType === 'Percentage' ? 'Enter percentage' : 'Enter amount'}
                />
              </Form.Group>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Min Amount <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={formData.minAmount}
                  onChange={(e) => setFormData({ ...formData, minAmount: e.target.value })}
                  placeholder="Enter minimum amount"
                />
              </Form.Group>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Max Amount <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={formData.maxAmount}
                  onChange={(e) => setFormData({ ...formData, maxAmount: e.target.value })}
                  placeholder="Enter maximum amount"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={handleSaveCommission}
            className="btn-outline-primary-custom"
          >
            {modalMode === 'add' ? 'Add Commission' : 'Update Commission'}
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => setShowModal(false)}
            className="btn-outline-danger-custom"
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default APIProviderCommission;

